import { useMemo } from 'react';
import { useAuthStore } from '../../../../common/auth/store';
import { useGetMyStudents } from '../../../../common/api/endpoints/users/users';
import { useEngagementStore } from '../EngagementStore';
import { StudentEngagementRecord } from '../types';
import type { UserShort } from '../../../../common/api/models/userShort';

/**
 * Data orchestration hook for the Engagement feature.
 * Fetches students, then applies frontend filters and groupings.
 */
export const useEngagementData = () => {
  const userId = useAuthStore((state) => state.user?.id);
  const filter = useEngagementStore((state) => state.filter);

  // 1. Fetch Students
  const { data: studentsData, isLoading: isStudentsLoading } = useGetMyStudents(userId!, {
    query: {
      enabled: !!userId,
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
    },
  });

  const records = studentsData?.data?.data?.records;

  const rawRecords = useMemo(() => records ?? [], [records]);

  const isEngagementWorkState = (workState: StudentEngagementRecord['workState']) => (
    workState === 'active' || workState === 'stalled' || workState === 'waiting'
  );

  const workStateFilter = filter.workState ?? 'all';

  // Extract unique modules for filtering
  const availableModules = useMemo(() => {
    const modulesMap = new Map<string, { id: string; title: string }>();
    rawRecords.forEach((record) => {
      // Only include modules where there are active/stalled students
      if (isEngagementWorkState(record.workState)) {
        if (!modulesMap.has(record.moduleId)) {
          modulesMap.set(record.moduleId, { id: record.moduleId, title: record.moduleTitle });
        }
      }
    });
    return Array.from(modulesMap.values()).sort((a, b) => a.title.localeCompare(b.title));
  }, [rawRecords]);

  const availableVersions = useMemo(() => {
    if (!filter.moduleId) return [];
    const versions = new Set<string>();
    rawRecords.forEach((record) => {
      if (record.moduleId === filter.moduleId && record.moduleVersion) {
        versions.add(record.moduleVersion);
      }
    });
    return Array.from(versions).sort();
  }, [rawRecords, filter.moduleId]);

  // 2. Apply Filters at record level, then group by student
  const filteredRecords = useMemo(() => rawRecords.filter((record) => {
    if (filter.moduleId && record.moduleId !== filter.moduleId) return false;
    if (filter.moduleId && filter.moduleVersion && record.moduleVersion !== filter.moduleVersion) {
      return false;
    }
    if (workStateFilter === 'active') {
      return record.workState === 'active' || record.workState === 'waiting';
    }
    if (workStateFilter === 'stalled') {
      return record.workState === 'stalled';
    }
    return isEngagementWorkState(record.workState);
  }), [rawRecords, filter.moduleId, filter.moduleVersion, workStateFilter]);

  // 3. Group by Student (Human-Centric)
  const groupedByStudent = useMemo(() => {
    const groups: Record<string, { student: UserShort; modules: StudentEngagementRecord[] }> = {};

    filteredRecords.forEach((record) => {
      const sId = record.student.id;
      if (!groups[sId]) {
        groups[sId] = { student: record.student, modules: [] };
      }
      groups[sId].modules.push(record);
    });

    return Object.values(groups).sort((a, b) => {
      // Sort by active first, then stalled
      const aIsActive = a.modules.some((m) => m.workState === 'active' || m.workState === 'waiting');
      const bIsActive = b.modules.some((m) => m.workState === 'active' || m.workState === 'waiting');
      const aIsStalled = a.modules.some((m) => m.workState === 'stalled') && !aIsActive;
      const bIsStalled = b.modules.some((m) => m.workState === 'stalled') && !bIsActive;

      // If a is stalled and b is not, a goes down (return 1)
      if (aIsStalled && !bIsStalled) return 1;
      // If a is not stalled and b is stalled, a goes up (return -1)
      if (!aIsStalled && bIsStalled) return -1;

      // If both are the same state, sort by last activity (most recent first)
      const aLastActivity = Math.max(...a.modules.map((m) => (m.lastActivity ? new Date(m.lastActivity as string).getTime() : 0)));
      const bLastActivity = Math.max(...b.modules.map((m) => (m.lastActivity ? new Date(m.lastActivity as string).getTime() : 0)));

      if (aLastActivity !== bLastActivity) {
        return bLastActivity - aLastActivity;
      }

      // If both are the same state and have no activity or same activity, sort alphabetically by student lastname
      return (a.student.lastname || '').localeCompare(b.student.lastname || '');
    });
  }, [filteredRecords]);

  // 4. Stalled Count
  const stalledCount = useMemo(
    () => groupedByStudent.filter((group) => {
      const isActive = group.modules.some((m) => m.workState === 'active' || m.workState === 'waiting');
      return group.modules.some((m) => m.workState === 'stalled') && !isActive;
    }).length,
    [groupedByStudent],
  );

  return {
    groupedByStudent,
    stalledCount,
    availableModules,
    availableVersions,
    isLoading: isStudentsLoading,
  };
};

export default useEngagementData;
